import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, MapPin, Package, DollarSign, Check, Loader2, X, Search, Zap, Clock, Rocket, Truck, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const REGION_MAP = {
  'Alberta': 'AB', 'British Columbia': 'BC', 'Manitoba': 'MB', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Nova Scotia': 'NS', 'Ontario': 'ON',
  'Prince Edward Island': 'PE', 'Quebec': 'QC', 'Saskatchewan': 'SK',
  'Northwest Territories': 'NT', 'Nunavut': 'NU', 'Yukon': 'YT',
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
  'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
  'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
  'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
  'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
  'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
  'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
};

const STEPS = [
  { id: 'pickup', label: 'Pickup', icon: MapPin },
  { id: 'delivery', label: 'Delivery', icon: MapPin },
  { id: 'cargo', label: 'Cargo', icon: Package },
  { id: 'review', label: 'Review', icon: DollarSign },
];

function AddressStep({ title, subtitle, address, onChange, onGeocode, suggestions, onSelectSuggestion, loading }) {
  return (
    <div className="space-y-5 animate-slide-up">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="step-title">{title}</h2>
        <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground font-medium">Location Name</label>
        <Input
          value={address.name || ''}
          onChange={e => onChange({ ...address, name: e.target.value })}
          placeholder="e.g. Main Warehouse, Toronto Hub"
          className="rounded-xl h-12 text-base border-input bg-muted text-foreground focus:border-brand-accent mt-1"
          data-testid="address-name"
        />
      </div>
      <div className="relative">
        <label className="text-xs text-muted-foreground font-medium">Search Address</label>
        <div className="flex items-center gap-2 relative mt-1">
          <Input
            value={address.search || ''}
            onChange={e => { onChange({ ...address, search: e.target.value }); onGeocode(e.target.value); }}
            placeholder="Start typing an address..."
            className="rounded-xl h-12 text-base border-input bg-muted text-foreground focus:border-brand-accent pl-10"
            data-testid="address-search"
          />
          <Search className="w-5 h-5 text-muted-foreground absolute left-3 top-3.5" />
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-foreground absolute right-3 top-[42px]" />}
        {suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-muted border border-input rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestions">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => onSelectSuggestion(s)}
                className="w-full text-left px-4 py-3 text-sm text-muted-foreground hover:bg-background border-b border-input/50 last:border-0 transition-colors"
                data-testid={`suggestion-${i}`}>
                <MapPin className="w-3.5 h-3.5 text-foreground inline mr-2 -mt-0.5" />
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs text-muted-foreground font-medium">Street Address</label>
          <Input value={address.street} onChange={e => onChange({ ...address, street: e.target.value })}
            placeholder="123 Main St" className="rounded-xl h-11 mt-1" data-testid="address-street" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs text-muted-foreground font-medium">Unit / Suite #</label>
          <Input value={address.unit || ''} onChange={e => onChange({ ...address, unit: e.target.value })}
            placeholder="Unit 4B" className="rounded-xl h-11 mt-1" data-testid="address-unit" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">City</label>
          <Input value={address.city} onChange={e => onChange({ ...address, city: e.target.value })}
            placeholder="Toronto" className="rounded-xl h-11 mt-1" data-testid="address-city" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Province</label>
          <select value={address.province} onChange={e => onChange({ ...address, province: e.target.value })}
            className="w-full h-11 mt-1 rounded-xl border border-input px-3 text-sm bg-muted text-foreground focus:outline-none focus:border-brand-accent"
            data-testid="address-province">
            <option value="">Select...</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Postal Code</label>
          <Input value={address.postal} onChange={e => onChange({ ...address, postal: e.target.value })}
            placeholder="M5V 2T6" className="rounded-xl h-11 mt-1" data-testid="address-postal" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Contact Name</label>
          <Input value={address.contactName || ''} onChange={e => onChange({ ...address, contactName: e.target.value })}
            placeholder="John Smith" className="rounded-xl h-11 mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Contact Phone</label>
          <Input value={address.contactPhone || ''} onChange={e => onChange({ ...address, contactPhone: e.target.value })}
            placeholder="416-555-0100" className="rounded-xl h-11 mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Date</label>
          <Input type="date" value={address.date || ''} onChange={e => onChange({ ...address, date: e.target.value })}
            className="rounded-xl h-11 mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-medium">Hours / Time Window</label>
          <Input value={address.hours || ''} onChange={e => onChange({ ...address, hours: e.target.value })}
            placeholder="9am - 5pm" className="rounded-xl h-11 mt-1" />
        </div>
      </div>
    </div>
  );
}

const DELIVERY_TYPES = [
  { id: 'regular', label: 'Regular', time: 'Within 5 hours', icon: Clock, multiplier: 1.0, color: 'border-input bg-muted/50', activeColor: 'border-brand-accent bg-brand-accent/10 ring-2 ring-brand-accent' },
  { id: 'rush', label: 'Rush', time: 'Within 1-2 hours', icon: Zap, multiplier: 1.5, color: 'border-input bg-muted/50', activeColor: 'border-amber-500 bg-amber-500/10 ring-2 ring-amber-500' },
  { id: 'direct', label: 'Direct', time: 'Within 1 hour — dedicated', icon: Rocket, multiplier: 2.0, color: 'border-input bg-muted/50', activeColor: 'border-red-500 bg-red-500/10 ring-2 ring-red-500' },
];

function getVehicleType(weight) {
  if (!weight || weight <= 0) return null;
  if (weight <= 5000) return { type: 'cargo_van', label: 'Cargo Van', desc: 'Up to 5,000 lbs' };
  if (weight <= 15000) return { type: 'straight_truck', label: 'Straight Truck', desc: '5,001 - 15,000 lbs' };
  if (weight <= 30000) return { type: 'truck', label: 'Truck', desc: '15,001 - 30,000 lbs' };
  if (weight <= 44000) return { type: 'flatbed', label: 'Flatbed / Semi', desc: '30,001 - 44,000 lbs' };
  return { type: 'oversize', label: 'Oversize Load', desc: '44,001+ lbs — special permit required' };
}

const PACKAGE_TYPES = ['box', 'pallet', 'skid', 'crate', 'drum', 'other'];
const emptyPackage = () => ({ package_type: 'box', quantity: 1, weight_lbs: '', length_in: '', width_in: '', height_in: '', description: '' });

function CargoStep({ cargo, onChange, packages, onPackagesChange }) {
  const vehicle = getVehicleType(cargo.weight);

  const updatePkg = (idx, field, value) => {
    const updated = [...packages];
    updated[idx] = { ...updated[idx], [field]: value };
    onPackagesChange(updated);
  };

  return (
    <div className="space-y-5 animate-slide-up">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="step-title">What are you shipping?</h2>
        <p className="text-muted-foreground text-sm mt-1">Tell us about your cargo and delivery speed</p>
      </div>

      {/* Delivery Type Cards */}
      <div>
        <label className="text-xs text-muted-foreground font-medium mb-2 block">Delivery Speed</label>
        <div className="grid grid-cols-3 gap-3">
          {DELIVERY_TYPES.map(dt => (
            <button key={dt.id} onClick={() => onChange({ ...cargo, delivery_type: dt.id })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                cargo.delivery_type === dt.id ? dt.activeColor : dt.color + ' hover:border-muted-foreground'
              }`} data-testid={`delivery-type-${dt.id}`}>
              <dt.icon className={`w-5 h-5 mb-2 ${
                dt.id === 'rush' ? 'text-amber-500' : dt.id === 'direct' ? 'text-red-500' : 'text-foreground'
              }`} />
              <p className="text-sm font-bold text-foreground">{dt.label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{dt.time}</p>
              {dt.multiplier > 1 && (
                <p className="text-[10px] font-bold mt-1.5 text-muted-foreground">{dt.multiplier}x rate</p>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-medium">Cargo Description</label>
        <Textarea value={cargo.description} onChange={e => onChange({ ...cargo, description: e.target.value })}
          placeholder="e.g., 20 pallets of auto parts, fragile electronics, construction lumber..."
          className="rounded-xl mt-1 min-h-[100px] text-base" data-testid="cargo-description" />
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-medium">Total Weight (lbs)</label>
        <Input type="number" value={cargo.weight || ''} onChange={e => {
          const w = +e.target.value;
          const v = getVehicleType(w);
          onChange({ ...cargo, weight: w, equipment_type: v?.type || cargo.equipment_type });
        }}
          placeholder="e.g., 35000" className="rounded-xl h-12 mt-1 text-base" data-testid="cargo-weight" />
        {vehicle && (
          <div className="mt-2 flex items-center gap-2 bg-muted/50 border border-input rounded-xl px-3 py-2.5">
            <Truck className="w-4 h-4 text-foreground flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-bold text-muted-foreground">Recommended: {vehicle.label}</p>
              <p className="text-[10px] text-muted-foreground">{vehicle.desc}</p>
            </div>
            <select value={cargo.equipment_type || vehicle.type}
              onChange={e => onChange({ ...cargo, equipment_type: e.target.value })}
              className="text-[11px] bg-muted border border-input rounded-lg px-2 py-1 text-muted-foreground">
              <option value="cargo_van">Cargo Van</option>
              <option value="straight_truck">Straight Truck</option>
              <option value="truck">Truck</option>
              <option value="flatbed">Flatbed / Semi</option>
              <option value="reefer">Reefer</option>
              <option value="oversize">Oversize</option>
            </select>
          </div>
        )}
      </div>

      {/* Packages */}
      <div>
        <label className="text-xs text-muted-foreground font-medium mb-2 block">Packages</label>
        <div className="space-y-3">
          {packages.map((pkg, idx) => (
            <div key={idx} className="bg-muted/50 border border-input rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <select value={pkg.package_type} onChange={e => updatePkg(idx, 'package_type', e.target.value)}
                  className="h-9 rounded-lg border border-input px-2 text-sm bg-muted text-foreground flex-1">
                  {PACKAGE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <Input type="number" value={pkg.quantity} onChange={e => updatePkg(idx, 'quantity', +e.target.value || 1)}
                  placeholder="Qty" className="rounded-lg h-9 w-16 text-sm" min={1} />
                <Input type="number" value={pkg.weight_lbs} onChange={e => updatePkg(idx, 'weight_lbs', e.target.value)}
                  placeholder="Weight (lbs)" className="rounded-lg h-9 w-28 text-sm" />
                {packages.length > 1 && (
                  <button onClick={() => onPackagesChange(packages.filter((_, i) => i !== idx))}
                    className="text-muted-foreground hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground font-medium w-8">L×W×H</span>
                <Input type="number" value={pkg.length_in} onChange={e => updatePkg(idx, 'length_in', e.target.value)}
                  placeholder="L" className="rounded-lg h-8 w-16 text-xs" />
                <span className="text-muted-foreground text-xs">×</span>
                <Input type="number" value={pkg.width_in} onChange={e => updatePkg(idx, 'width_in', e.target.value)}
                  placeholder="W" className="rounded-lg h-8 w-16 text-xs" />
                <span className="text-muted-foreground text-xs">×</span>
                <Input type="number" value={pkg.height_in} onChange={e => updatePkg(idx, 'height_in', e.target.value)}
                  placeholder="H" className="rounded-lg h-8 w-16 text-xs" />
                <span className="text-[10px] text-muted-foreground">in</span>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => onPackagesChange([...packages, emptyPackage()])}
          className="flex items-center gap-1.5 text-sm text-brand-accent hover:text-brand-accent/80 font-medium mt-2">
          <Plus className="w-4 h-4" /> Add Package
        </button>
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-medium">Special Notes (optional)</label>
        <Textarea value={cargo.notes} onChange={e => onChange({ ...cargo, notes: e.target.value })}
          placeholder="Any special handling, timing requirements, etc."
          className="rounded-xl mt-1 min-h-[80px]" data-testid="cargo-notes" />
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-medium">Bill To (optional)</label>
        <Input value={cargo.billTo || ''} onChange={e => onChange({ ...cargo, billTo: e.target.value })}
          placeholder="Company or person to bill" className="rounded-xl h-11 mt-1" />
      </div>

      <div className="flex items-center justify-between p-3 rounded-xl border border-input bg-muted/50">
        <div>
          <p className="text-sm font-medium text-foreground">Signature Required on Delivery</p>
          <p className="text-xs text-muted-foreground">Driver must collect a signature from the receiver</p>
        </div>
        <button
          type="button"
          onClick={() => onChange({ ...cargo, signatureRequired: !cargo.signatureRequired })}
          className={`w-11 h-6 rounded-full transition-colors ${cargo.signatureRequired !== false ? 'bg-brand-accent' : 'bg-muted-foreground/30'}`}>
          <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${cargo.signatureRequired !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>
    </div>
  );
}

function ReviewStep({ pickup, delivery, cargo, pricing, pricingLoading }) {
  const formatAddr = (a) => [a.street, a.city, a.province, a.postal].filter(Boolean).join(', ');
  const dt = DELIVERY_TYPES.find(d => d.id === cargo.delivery_type) || DELIVERY_TYPES[0];
  const baseRate = pricing?.estimated_rate || 0;
  const surcharge = Math.round(baseRate * (dt.multiplier - 1));
  const totalRate = Math.round(baseRate * dt.multiplier);
  const vehicle = getVehicleType(cargo.weight);

  return (
    <div className="space-y-5 animate-slide-up">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="step-title">Review & Confirm</h2>
        <p className="text-muted-foreground text-sm mt-1">Here's your shipment summary</p>
      </div>

      <div className="bg-muted/50 rounded-2xl p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-xs text-muted-foreground font-medium">PICKUP</span>
          </div>
          {pickup.name && <p className="text-sm font-bold text-foreground ml-[18px]" data-testid="review-pickup-name">{pickup.name}</p>}
          <p className="text-sm font-medium text-muted-foreground ml-[18px]" data-testid="review-pickup">{formatAddr(pickup)}</p>
          {pickup.contactName && <p className="text-xs text-muted-foreground ml-[18px] mt-0.5">Contact: {pickup.contactName}{pickup.contactPhone ? ` — ${pickup.contactPhone}` : ''}</p>}
          {(pickup.date || pickup.hours) && <p className="text-xs text-muted-foreground ml-[18px] mt-0.5">{pickup.date && `Date: ${pickup.date}`}{pickup.date && pickup.hours ? ' · ' : ''}{pickup.hours && `Hours: ${pickup.hours}`}</p>}
        </div>
        <div className="ml-[6px] border-l-2 border-dashed border-muted-foreground h-4" />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="text-xs text-muted-foreground font-medium">DELIVERY</span>
          </div>
          {delivery.name && <p className="text-sm font-bold text-foreground ml-[18px]" data-testid="review-delivery-name">{delivery.name}</p>}
          <p className="text-sm font-medium text-muted-foreground ml-[18px]" data-testid="review-delivery">{formatAddr(delivery)}</p>
          {delivery.contactName && <p className="text-xs text-muted-foreground ml-[18px] mt-0.5">Contact: {delivery.contactName}{delivery.contactPhone ? ` — ${delivery.contactPhone}` : ''}</p>}
          {(delivery.date || delivery.hours) && <p className="text-xs text-muted-foreground ml-[18px] mt-0.5">{delivery.date && `Date: ${delivery.date}`}{delivery.date && delivery.hours ? ' · ' : ''}{delivery.hours && `Hours: ${delivery.hours}`}</p>}
        </div>
      </div>

      <div className="bg-muted/50 rounded-2xl p-5">
        <p className="text-xs text-muted-foreground font-medium mb-2">CARGO & DELIVERY</p>
        <p className="text-sm font-medium" data-testid="review-cargo">{cargo.description}</p>
        <p className="text-xs text-muted-foreground mt-1">{cargo.weight?.toLocaleString()} lbs</p>
        {cargo.notes && <p className="text-xs text-muted-foreground mt-1 italic">{cargo.notes}</p>}
        <div className="flex items-center gap-3 mt-3">
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg ${
            dt.id === 'rush' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' : dt.id === 'direct' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' : 'bg-brand-accent/10 text-foreground'
          }`}>
            <dt.icon className="w-3.5 h-3.5" /> {dt.label} — {dt.time}
          </span>
          {vehicle && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-muted text-muted-foreground">
              <Truck className="w-3.5 h-3.5" /> {vehicle.label}
            </span>
          )}
        </div>
      </div>

      <div className="bg-brand-accent/5 border border-border rounded-2xl p-5" data-testid="pricing-card">
        <p className="text-xs text-foreground font-medium mb-3">ESTIMATED PRICING</p>
        {pricingLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Calculating route...
          </div>
        ) : pricing ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Distance</span>
              <span className="font-semibold" data-testid="review-distance">{pricing.distance_miles} mi</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Est. Duration</span>
              <span className="font-semibold" data-testid="review-duration">{Math.floor(pricing.duration_minutes / 60)}h {Math.round(pricing.duration_minutes % 60)}m</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Base Rate</span>
              <span className="font-semibold">${baseRate.toLocaleString()}</span>
            </div>
            {surcharge > 0 && (
              <div className="flex justify-between text-sm">
                <span className={`${dt.id === 'rush' ? 'text-amber-500' : 'text-red-500'} font-medium`}>
                  {dt.label} Surcharge ({dt.multiplier}x)
                </span>
                <span className={`font-semibold ${dt.id === 'rush' ? 'text-amber-500' : 'text-red-500'}`}>+${surcharge.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-border pt-2 mt-2 flex justify-between">
              <span className="text-muted-foreground font-medium">Total Estimated Rate</span>
              <span className="text-2xl font-bold text-foreground" data-testid="review-rate">${totalRate.toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to calculate - please verify addresses</p>
        )}
      </div>
    </div>
  );
}

export default function ShipmentWizard({ token, user, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState({ search: '', name: '', street: '', city: '', province: '', postal: '', unit: '', lat: null, lng: null, contactName: '', contactPhone: '', date: '', hours: '' });
  const [delivery, setDelivery] = useState({ search: '', name: '', street: '', city: '', province: '', postal: '', unit: '', lat: null, lng: null, contactName: '', contactPhone: '', date: '', hours: '' });
  const [cargo, setCargo] = useState({ description: '', weight: 0, notes: '', delivery_type: 'regular', equipment_type: '', billTo: '', signatureRequired: true });
  const [packages, setPackages] = useState([emptyPackage()]);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultLoaded, setDefaultLoaded] = useState(false);
  const debounceRef = useRef(null);

  // Load default pickup from profile
  useEffect(() => {
    if (defaultLoaded) return;
    setDefaultLoaded(true);
    api.get('/profile').then(res => {
      const p = res.data;
      if (p.default_pickup_lat) {
        setPickup({
          search: p.default_pickup_address || '',
          name: p.default_pickup_name || '',
          street: p.default_pickup_street || '',
          city: p.default_pickup_city || '',
          province: p.default_pickup_province || '',
          postal: p.default_pickup_postal || '',
          lat: p.default_pickup_lat,
          lng: p.default_pickup_lng,
        });
      }
    }).catch(() => {});
  }, [token, defaultLoaded]);

  const geocode = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setGeoLoading(true);
      try {
        const res = await api.post('/routing/geocode', { address: query });
        setSuggestions(res.data || []);
      } catch { setSuggestions([]); }
      setGeoLoading(false);
    }, 400);
  }, [token]);

  const selectSuggestion = (s, isPickup) => {
    const parts = s.display_name.split(', ');
    const city = parts[0] || '';
    // Match full state/province name from Nominatim (e.g., "Ontario" → "ON", "California" → "CA")
    let prov = '';
    for (const pt of parts) {
      if (REGION_MAP[pt.trim()]) { prov = REGION_MAP[pt.trim()]; break; }
    }
    // Fallback: check if any part is already a 2-letter code
    if (!prov) { prov = PROVINCES.find(p => parts.some(pt => pt.trim() === p)) || ''; }
    const currentData = isPickup ? pickup : delivery;
    const data = {
      search: s.display_name,
      name: currentData.name || '',
      street: parts[0] || '',
      city: parts.length > 1 ? parts[0] : city,
      province: prov,
      postal: '',
      lat: s.lat,
      lng: s.lng,
    };
    if (isPickup) setPickup(data);
    else setDelivery(data);
    setSuggestions([]);
  };

  // Calculate pricing when reaching review step
  useEffect(() => {
    if (step === 3 && pickup.lat != null && delivery.lat != null) {
      setPricingLoading(true);
      api.post('/routing/distance', {
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        delivery_lat: delivery.lat, delivery_lng: delivery.lng,
      }).then(res => setPricing(res.data))
        .catch(() => setPricing(null))
        .finally(() => setPricingLoading(false));
    }
  }, [step, pickup.lat, pickup.lng, delivery.lat, delivery.lng, token]);

  const formatAddr = (a) => [a.street, a.city, a.province, a.postal].filter(Boolean).join(', ');

  const canNext = () => {
    if (step === 0) return pickup.city && pickup.lat != null;
    if (step === 1) return delivery.city && delivery.lat != null;
    if (step === 2) return cargo.description && cargo.weight > 0;
    return !!pricing;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
            const dt = DELIVERY_TYPES.find(d => d.id === cargo.delivery_type) || DELIVERY_TYPES[0];
      const finalRate = Math.round((pricing.estimated_rate || 0) * dt.multiplier);
      const vehicle = cargo.equipment_type || getVehicleType(cargo.weight)?.type || null;
      const validPkgs = packages.filter(p => p.weight_lbs || p.description || p.quantity > 1);
      const res = await api.post('/loads', {
        org_id: user.org_id,
        client_id: user.user_id,
        pickup_name: pickup.name || null,
        pickup_address: formatAddr(pickup),
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        pickup_unit: pickup.unit || null,
        delivery_name: delivery.name || null,
        delivery_address: formatAddr(delivery),
        delivery_lat: delivery.lat,
        delivery_lng: delivery.lng,
        delivery_unit: delivery.unit || null,
        shipper_contact_name: pickup.contactName || null,
        shipper_contact_phone: pickup.contactPhone || null,
        pickup_date: pickup.date || null,
        pickup_hours: pickup.hours || null,
        receiver_contact_name: delivery.contactName || null,
        receiver_contact_phone: delivery.contactPhone || null,
        delivery_date: delivery.date || null,
        delivery_hours: delivery.hours || null,
        bill_to: cargo.billTo || null,
        signature_required: cargo.signatureRequired !== false,
        cargo_description: cargo.description,
        weight_lbs: cargo.weight,
        rate: finalRate,
        distance_miles: pricing.distance_miles,
        notes: cargo.notes || null,
        delivery_type: cargo.delivery_type || 'regular',
        equipment_type_required: vehicle,
        packages: validPkgs.length > 0 ? validPkgs : null,
      });
      await api.put(`/loads/${res.data.load_id}/status`, { status: 'posted' });
      toast.success('Shipment created and posted!');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shipment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background text-foreground flex flex-col" data-testid="shipment-wizard">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-4">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <button
                onClick={() => { if (i <= step) setStep(i); }}
                disabled={i > step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                i < step ? 'bg-brand-accent text-brand-accent-foreground cursor-pointer hover:ring-2 hover:ring-brand-accent/50' :
                i === step ? 'bg-brand-accent/10 text-brand-accent ring-2 ring-brand-accent' :
                'bg-muted text-muted-foreground cursor-not-allowed'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </button>
              <span className={`text-sm font-medium hidden sm:block ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-border hidden sm:block" />}
            </div>
          ))}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-2" data-testid="wizard-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-6 py-8">
          {step === 0 && (
            <AddressStep
              title="Where should we pick up?"
              subtitle="Enter the origin address for this shipment"
              address={pickup} onChange={setPickup}
              onGeocode={geocode} suggestions={suggestions}
              onSelectSuggestion={(s) => selectSuggestion(s, true)}
              loading={geoLoading}
            />
          )}
          {step === 1 && (
            <AddressStep
              title="Where is it going?"
              subtitle="Enter the destination for delivery"
              address={delivery} onChange={setDelivery}
              onGeocode={geocode} suggestions={suggestions}
              onSelectSuggestion={(s) => selectSuggestion(s, false)}
              loading={geoLoading}
            />
          )}
          {step === 2 && <CargoStep cargo={cargo} onChange={setCargo} packages={packages} onPackagesChange={setPackages} />}
          {step === 3 && (
            <ReviewStep pickup={pickup} delivery={delivery} cargo={cargo}
              pricing={pricing} pricingLoading={pricingLoading} />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-6 py-4 flex items-center justify-between bg-sidebar">
        <Button variant="ghost" onClick={() => step > 0 ? setStep(step - 1) : onClose()}
          className="text-muted-foreground rounded-xl" data-testid="wizard-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step < 3 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext()}
            className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-semibold rounded-xl px-8 h-12"
            data-testid="wizard-next">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving || !pricing}
            className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-semibold rounded-xl px-8 h-12"
            data-testid="wizard-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Check className="w-4 h-4 mr-2" /> Create Shipment</>}
          </Button>
        )}
      </div>
    </div>
  );
}
